import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Reusable pager. Server owns pagination — this is just Prev / Next + counter.
 * Props:
 *   page          current 1-based page
 *   pageSize      rows per page
 *   total         total server-side matching rows
 *   onPage(n)     callback with next page number
 *   testid        data-testid prefix
 *   compact       optional (renders smaller)
 */
export default function Paginator({ page = 1, pageSize = 20, total = 0, onPage, testid = "paginator", compact = false }) {
  const totalPages = Math.max(1, Math.ceil((total || 0) / Math.max(1, pageSize)));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(total, page * pageSize);
  const prev = () => page > 1 && onPage(page - 1);
  const next = () => page < totalPages && onPage(page + 1);

  return (
    <div className={`flex items-center justify-between gap-3 flex-wrap ${compact ? "py-2" : "py-4"}`} data-testid={testid}>
      <div className="text-[11px] uppercase tracking-widest text-slate-500" data-testid={`${testid}-count`}>
        Showing <span className="text-white font-bold">{from}</span>–<span className="text-white font-bold">{to}</span> of <span className="text-amber-400 font-bold">{total}</span>
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={prev}
          disabled={page <= 1}
          data-testid={`${testid}-prev`}
          className="h-8 px-3 rounded-md bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-300 text-xs flex items-center gap-1 disabled:opacity-30 disabled:cursor-not-allowed"
        ><ChevronLeft className="w-3.5 h-3.5"/> Prev</button>
        <div className="text-[11px] uppercase tracking-widest text-slate-400" data-testid={`${testid}-page`}>
          Page <span className="text-white font-bold">{page}</span> / {totalPages}
        </div>
        <button
          type="button"
          onClick={next}
          disabled={page >= totalPages}
          data-testid={`${testid}-next`}
          className="h-8 px-3 rounded-md bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-300 text-xs flex items-center gap-1 disabled:opacity-30 disabled:cursor-not-allowed"
        >Next <ChevronRight className="w-3.5 h-3.5"/></button>
      </div>
    </div>
  );
}
